import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format, isSameDay, parseISO, isValid, startOfMonth, endOfMonth } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import {
    Phone, MessageCircle, Mail, MessageSquare, Users, Linkedin, MoreHorizontal,
    CheckSquare, AlertCircle, Loader2, Filter, ExternalLink,
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL
    ? `${process.env.REACT_APP_BACKEND_URL}/api`
    : (console.warn('REACT_APP_BACKEND_URL is not set; falling back to /api'), '/api');

const CHANNEL_META = {
    call:     { label: 'Call',     icon: Phone,          color: 'bg-blue-100 text-blue-700',   dot: 'bg-blue-500' },
    whatsapp: { label: 'WhatsApp', icon: MessageCircle,   color: 'bg-green-100 text-green-700', dot: 'bg-green-500' },
    email:    { label: 'Email',    icon: Mail,            color: 'bg-purple-100 text-purple-700', dot: 'bg-purple-500' },
    sms:      { label: 'SMS',      icon: MessageSquare,   color: 'bg-orange-100 text-orange-700', dot: 'bg-orange-400' },
    meeting:  { label: 'Meeting',  icon: Users,           color: 'bg-red-100 text-red-700',    dot: 'bg-red-500' },
    linkedin: { label: 'LinkedIn', icon: Linkedin,        color: 'bg-blue-100 text-blue-800',  dot: 'bg-blue-700' },
    other:    { label: 'Other',    icon: MoreHorizontal,  color: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' },
};

const OUTCOME_META = {
    interested:         { label: 'Interested',         color: 'bg-green-100 text-green-800' },
    not_interested:     { label: 'Not Interested',     color: 'bg-red-100 text-red-800' },
    no_answer:          { label: 'No Answer',          color: 'bg-slate-100 text-slate-600' },
    callback_requested: { label: 'Callback Requested', color: 'bg-yellow-100 text-yellow-800' },
    follow_up_needed:   { label: 'Follow-up Needed',   color: 'bg-blue-100 text-blue-800' },
    deal_closed:        { label: 'Deal Closed',        color: 'bg-emerald-100 text-emerald-800' },
};

const TASK_TYPE_META = {
    Call:     { icon: Phone,         color: 'bg-blue-100 text-blue-700' },
    WhatsApp: { icon: MessageCircle, color: 'bg-green-100 text-green-700' },
    Email:    { icon: Mail,          color: 'bg-purple-100 text-purple-700' },
    SMS:      { icon: MessageSquare, color: 'bg-orange-100 text-orange-700' },
    Meeting:  { icon: Users,         color: 'bg-red-100 text-red-700' },
    LinkedIn: { icon: Linkedin,      color: 'bg-blue-100 text-blue-800' },
    Todo:     { icon: CheckSquare,   color: 'bg-slate-100 text-slate-700' },
};

const safeParseDate = (str) => {
    if (!str) return null;
    try { const d = parseISO(str); return isValid(d) ? d : null; }
    catch { return null; }
};

const getInitials = (email) => {
    if (!email) return '?';
    return email.split('@')[0].substring(0, 2).toUpperCase();
};

const CalendarPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tasks, setTasks] = useState([]);
    const [interactions, setInteractions] = useState([]);
    const [teamMembers, setTeamMembers] = useState([]);
    const [selectedDay, setSelectedDay] = useState(new Date());
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [assigneeFilter, setAssigneeFilter] = useState('me');

    const fetchTeamMembers = async () => {
        try {
            const res = await axios.get(`${API_URL}/team/members`);
            setTeamMembers(res.data);
        } catch (e) { console.error('Failed to load team members:', e); }
    };

    useEffect(() => {
        fetchTeamMembers();
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        const fetchData = async () => {
            try {
                setLoading(true);
                const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
                const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');

                const params = { start_date: start, end_date: end };
                if (assigneeFilter === 'me' && user?.id) params.assignee_id = user.id;
                else if (assigneeFilter !== 'all') params.assignee_id = assigneeFilter;

                const res = await axios.get(`${API_URL}/calendar/events`, { params, signal: controller.signal });
                setTasks(res.data.tasks || []);
                setInteractions(res.data.interactions || []);
            } catch (e) {
                if (!axios.isCancel(e) && e.name !== 'CanceledError' && e.name !== 'AbortError') {
                    toast.error('Failed to load calendar events');
                }
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        fetchData();
        return () => controller.abort();
    }, [currentMonth, assigneeFilter, user]);

    const eventsByDate = useMemo(() => {
        const map = {};
        for (const t of tasks) {
            const d = safeParseDate(t.due_date);
            if (!d) continue;
            const key = format(d, 'yyyy-MM-dd');
            if (!map[key]) map[key] = { tasks: [], interactions: [] };
            map[key].tasks.push(t);
        }
        for (const i of interactions) {
            const d = safeParseDate(i.occurred_at);
            if (!d) continue;
            const key = format(d, 'yyyy-MM-dd');
            if (!map[key]) map[key] = { tasks: [], interactions: [] };
            map[key].interactions.push(i);
        }
        return map;
    }, [tasks, interactions]);

    const selectedKey = selectedDay ? format(selectedDay, 'yyyy-MM-dd') : null;
    const selectedEvents = selectedKey ? (eventsByDate[selectedKey] || { tasks: [], interactions: [] }) : { tasks: [], interactions: [] };

    const interactionDays = interactions.map(i => safeParseDate(i.occurred_at)).filter(Boolean);
    const taskDays = tasks.map(t => safeParseDate(t.due_date)).filter(Boolean);
    const overdueDays = tasks
        .filter(t => t.is_overdue)
        .map(t => safeParseDate(t.due_date))
        .filter(Boolean);

    const modifiers = {
        hasInteraction: (day) => interactionDays.some(d => isSameDay(d, day)),
        hasTask: (day) => taskDays.some(d => isSameDay(d, day)),
        hasOverdue: (day) => overdueDays.some(d => isSameDay(d, day)),
    };

    const modifiersClassNames = {
        hasInteraction: 'rdp-day--has-interaction',
        hasTask: 'rdp-day--has-task',
        hasOverdue: 'rdp-day--has-overdue',
    };

    const totalToday = (() => {
        const key = format(new Date(), 'yyyy-MM-dd');
        const ev = eventsByDate[key] || { tasks: [], interactions: [] };
        return ev.tasks.length + ev.interactions.length;
    })();

    return (
        <div className="h-screen flex flex-col">
            {/* Header */}
            <div className="border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div>
                    <h1 className="text-xl font-semibold text-slate-900">Follow-up Calendar</h1>
                    <p className="text-sm text-slate-500 mt-0.5">
                        {totalToday > 0 ? `${totalToday} item${totalToday !== 1 ? 's' : ''} today` : 'All upcoming follow-ups and past interactions'}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Filter className="h-4 w-4 text-slate-400" />
                    <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                        <SelectTrigger className="w-44 text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="me">My tasks</SelectItem>
                            <SelectItem value="all">All team</SelectItem>
                            {teamMembers.map(m => (
                                <SelectItem key={m.id} value={m.id}>{m.email}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="flex flex-1 min-h-0">
                {/* Left: Calendar */}
                <div className="w-80 border-r border-slate-200 p-5 flex-shrink-0 overflow-y-auto">
                    <style>{`
                        .rdp-day--has-interaction { position: relative; }
                        .rdp-day--has-interaction::after {
                            content: '';
                            position: absolute;
                            bottom: 2px; left: calc(50% - 4px);
                            width: 4px; height: 4px;
                            border-radius: 50%;
                            background: #3b82f6;
                        }
                        .rdp-day--has-task::before {
                            content: '';
                            position: absolute;
                            bottom: 2px; left: calc(50% + 2px);
                            width: 4px; height: 4px;
                            border-radius: 50%;
                            background: #f97316;
                        }
                        .rdp-day--has-overdue::before {
                            background: #ef4444 !important;
                        }
                        .rdp-day--has-interaction.rdp-day--has-task::after { left: calc(50% - 6px); }
                    `}</style>
                    {loading ? (
                        <div className="flex items-center justify-center h-56">
                            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                        </div>
                    ) : (
                        <DayPicker
                            mode="single"
                            selected={selectedDay}
                            onSelect={setSelectedDay}
                            month={currentMonth}
                            onMonthChange={setCurrentMonth}
                            modifiers={modifiers}
                            modifiersClassNames={modifiersClassNames}
                            className="w-full"
                        />
                    )}

                    {/* Legend */}
                    <div className="mt-4 space-y-2 text-xs text-slate-500 border-t pt-3">
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-500 inline-block flex-shrink-0" />
                            Past interaction logged
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-orange-500 inline-block flex-shrink-0" />
                            Follow-up task due
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block flex-shrink-0" />
                            Overdue task
                        </div>
                    </div>

                    {/* Month summary */}
                    <div className="mt-4 border-t pt-3 space-y-1">
                        <p className="text-xs font-medium text-slate-600 mb-2">This month</p>
                        <div className="text-xs text-slate-500 flex justify-between">
                            <span>Tasks due</span>
                            <span className="font-medium text-slate-700">{tasks.length}</span>
                        </div>
                        <div className="text-xs text-slate-500 flex justify-between">
                            <span>Overdue</span>
                            <span className="font-medium text-red-600">{tasks.filter(t => t.is_overdue).length}</span>
                        </div>
                        <div className="text-xs text-slate-500 flex justify-between">
                            <span>Interactions logged</span>
                            <span className="font-medium text-slate-700">{interactions.length}</span>
                        </div>
                    </div>
                </div>

                {/* Right: Day detail */}
                <div className="flex-1 p-6 overflow-y-auto">
                    <h2 className="text-base font-semibold text-slate-800 mb-4">
                        {selectedDay ? format(selectedDay, 'EEEE, MMMM d, yyyy') : 'Select a date'}
                    </h2>

                    {selectedEvents.tasks.length === 0 && selectedEvents.interactions.length === 0 ? (
                        <div className="text-center py-16 text-slate-400">
                            <p className="text-sm">No follow-ups or interactions on this day</p>
                        </div>
                    ) : (
                        <div className="space-y-3 max-w-2xl">
                            {/* Tasks */}
                            {selectedEvents.tasks.length > 0 && (
                                <div>
                                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                        Follow-up Tasks ({selectedEvents.tasks.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedEvents.tasks.map(task => {
                                            const meta = TASK_TYPE_META[task.task_type] || TASK_TYPE_META.Todo;
                                            const Icon = meta.icon;
                                            return (
                                                <div
                                                    key={task.id}
                                                    className={`rounded-lg border p-4 flex items-start gap-3 ${task.is_overdue ? 'border-red-200 bg-red-50' : 'border-orange-200 bg-orange-50'}`}
                                                >
                                                    <div className={`p-2 rounded-lg ${meta.color} flex-shrink-0`}>
                                                        <Icon className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-sm font-medium text-slate-800">{task.description}</span>
                                                            {task.is_overdue && (
                                                                <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                                                                    <AlertCircle className="h-3 w-3" /> Overdue
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-3 mt-1">
                                                            {task.lead_name && (
                                                                <button
                                                                    onClick={() => navigate(`/leads/${task.lead_id}`)}
                                                                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                                                >
                                                                    <ExternalLink className="h-3 w-3" />
                                                                    {task.lead_name}
                                                                </button>
                                                            )}
                                                            {task.assignee_email && (
                                                                <span className="flex items-center gap-1 text-xs text-slate-500">
                                                                    <Avatar className="w-4 h-4">
                                                                        <AvatarFallback className="text-[8px]">
                                                                            {getInitials(task.assignee_email)}
                                                                        </AvatarFallback>
                                                                    </Avatar>
                                                                    {task.assignee_email.split('@')[0]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Interactions */}
                            {selectedEvents.interactions.length > 0 && (
                                <div className={selectedEvents.tasks.length > 0 ? 'mt-4' : ''}>
                                    <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
                                        Interactions Logged ({selectedEvents.interactions.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedEvents.interactions.map(interaction => {
                                            const ch = CHANNEL_META[interaction.channel] || CHANNEL_META.other;
                                            const oc = OUTCOME_META[interaction.outcome] || { label: interaction.outcome, color: 'bg-slate-100 text-slate-700' };
                                            const Icon = ch.icon;
                                            return (
                                                <div key={interaction.id} className="rounded-lg border border-slate-200 bg-white p-4 flex items-start gap-3 shadow-sm">
                                                    <div className={`p-2 rounded-lg ${ch.color} flex-shrink-0`}>
                                                        <Icon className="h-4 w-4" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            <span className="text-sm font-medium text-slate-800">{ch.label}</span>
                                                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${oc.color}`}>{oc.label}</span>
                                                            {interaction.duration_minutes && (
                                                                <span className="text-xs text-slate-400">{interaction.duration_minutes} min</span>
                                                            )}
                                                        </div>
                                                        {interaction.notes && (
                                                            <p className="text-sm text-slate-600 mt-1 line-clamp-2">{interaction.notes}</p>
                                                        )}
                                                        <div className="flex items-center gap-3 mt-1">
                                                            {interaction.lead_name && (
                                                                <button
                                                                    onClick={() => navigate(`/leads/${interaction.lead_id}`)}
                                                                    className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
                                                                >
                                                                    <ExternalLink className="h-3 w-3" />
                                                                    {interaction.lead_name}
                                                                </button>
                                                            )}
                                                            {interaction.actor_email && (
                                                                <span className="text-xs text-slate-400">
                                                                    by {interaction.actor_email.split('@')[0]}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CalendarPage;
